import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Progress = ({ run }) => (
  <div className="border-l-4 border-foreground bg-muted/40 px-4 py-3 text-sm" data-testid="bulk-invite-progress">
    <div className="font-semibold">{run.finished_at ? "Invio completato" : "Invio in corso a gruppi di 10…"}</div>
    <div className="mt-1 font-mono text-xs">{run.total} account creati · {run.sent} inviti inviati · {run.queued} in coda{run.failed > 0 && ` · ${run.failed} non riusciti`}</div>
    {run.skipped.length > 0 && <div className="mt-2 text-xs text-muted-foreground">Saltati: {run.skipped.map((s) => `${s.name} (${s.reason})`).join("; ")}</div>}
  </div>
);

const Row = ({ c, checked, onToggle, company }) => (
  <label className={`grid grid-cols-[24px_1.3fr_1fr_1.2fr] items-center gap-2 border-b px-3 py-1.5 text-sm ${c.blocked ? "text-muted-foreground" : "cursor-pointer hover:bg-muted/40"}`} data-testid={`bulk-invite-row-${c.id}`}>
    <Checkbox checked={checked} disabled={!!c.blocked} onCheckedChange={onToggle} data-testid={`bulk-invite-check-${c.id}`} />
    <span><b>{c.last_name} {c.first_name}</b> <span className="text-xs text-muted-foreground">{company}</span></span>
    <span className="truncate text-xs">{c.job_title}</span>
    <span className={`font-mono text-xs ${c.blocked ? "break-words" : "truncate"}`}>{c.work_email}{c.blocked && <div className="font-sans text-[11px] text-amber-700">{c.blocked}</div>}</span>
  </label>
);

export const BulkInvite = ({ onDone }) => {
  const { nameOf, visibleCompanies } = useData();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [company, setCompany] = useState("all");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRun(null);
    api.get("/app-access/candidates").then((r) => { setRows(r.data); setSel(r.data.filter((c) => c.suggested).map((c) => c.id)); }).catch((e) => toast.error(errMsg(e)));
  }, [open]);
  useEffect(() => {
    if (!run || run.finished_at) return undefined;
    const t = setInterval(() => api.get(`/app-access/bulk/${run.id}`).then((r) => { setRun(r.data); if (r.data.finished_at) onDone(); }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = useMemo(() => rows.filter((c) => company === "all" || c.company_id === company), [rows, company]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const go = async () => {
    if (!window.confirm(`Creare ${sel.length} accessi all'app e inviare gli inviti alle email aziendali?`)) return;
    setBusy(true);
    try { const r = await api.post("/app-access/bulk", { employee_ids: sel }); setRun(r.data); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="bulk-invite-button"><Send className="mr-1 h-4 w-4" />Inviti app in blocco</Button>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="bulk-invite-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Inviti all'app in blocco</DialogTitle></DialogHeader>
          {run ? <Progress run={run} /> : <>
            <p className="text-sm text-muted-foreground">Dipendenti in forza con email aziendale e senza accesso all'app. Sono già spuntati quelli con mansione tecnica; gli impiegati vanno spuntati a mano. Gli inviti partono a gruppi di 10 con una breve pausa e scadono dopo 72 ore (poi parte il sollecito automatico).</p>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={company} onValueChange={setCompany}>
                <SelectTrigger className="w-56" data-testid="bulk-invite-company"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutte le società</SelectItem>{visibleCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={() => setSel((s) => [...new Set([...s, ...shown.filter((c) => !c.blocked).map((c) => c.id)])])} data-testid="bulk-invite-select-all">Spunta tutti</Button>
              <Button size="sm" variant="ghost" onClick={() => setSel((s) => s.filter((id) => !shown.some((c) => c.id === id)))} data-testid="bulk-invite-select-none">Togli tutti</Button>
              <span className="ml-auto text-sm" data-testid="bulk-invite-count"><b>{sel.length}</b> selezionati su {rows.filter((c) => !c.blocked).length}</span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto border">
              {shown.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Nessun dipendente da invitare.</p>
                : shown.map((c) => <Row key={c.id} c={c} checked={sel.includes(c.id)} onToggle={() => toggle(c.id)} company={nameOf("companies", c.company_id, "code")} />)}
            </div>
          </>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>{run ? "Chiudi" : "Annulla"}</Button>
            {!run && <Button onClick={go} disabled={busy || !sel.length} data-testid="bulk-invite-submit">Crea accessi e invia {sel.length} inviti</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
